import { html } from 'lit';
import { customElement } from 'lit/decorators.js';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './sector-diversification-chart.constants.js';
import type { SectorDiversificationChartContentObject } from './sector-diversification-chart.model.js';
import { BeaconChartBase } from '@trp-beacon-ui-kit/foundation/components';
import { Options } from 'highcharts';
import { restrictLastLegendItemClick } from '../../common/utils/sharedChartOptions.js';
import { dateShort } from '../../common/utils/formatDate.js';

/**
 * @tag pop-sector-diversification-chart-component
 * @property {SectorDiversificationChartChart} [componentObject] Sector Diversification chart content
 * @attribute { SectorDiversificationChartContentObject } [data-component-object] SectorDiversificationChart content object
 */
@customElement(Constants.TAG_NAME)
export class PopSectorDiversificationChartComponent extends PopBaseComponent {
  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * SectorDiversificationChart content object
   */
  componentObject?: SectorDiversificationChartContentObject;

  firstUpdated(): void {
    const chart = this.shadowRoot?.querySelector('beacon-area-chart');
    if (chart) {
      // This is necessary because the pointFormatter option for the tooltip is a function and doesn't
      // work correctly when only passed in the options attribute to the beacon-bar-chart element.
      chart.options = this.getOptions();
    }
  }

  private getSeriesData() {
    const sectors = this.getUniqueSectors();

    return sectors.map((sector) => {
      const data = this.componentObject?.itemList.flatMap((item) => {
        const sectorData = item.sectorDiversification.find(
          (s) => s.sector === sector,
        );

        if (!sectorData) {
          return null;
        }

        return {
          x: new Date(item.asOfDate).getTime(), // Convert date to timestamp for Highcharts
          y: sectorData.percentOfFund || 0,
          date: dateShort(item.asOfDate),
          name: sector,
        };
      });

      return {
        name: sector,
        data,
      };
    });
  }

  private getUniqueSectors() {
    const sectors = this.componentObject?.itemList.flatMap((item) =>
      item.sectorDiversification.map((sector) => sector.sector),
    );

    return Array.from(new Set(sectors || []));
  }

  getOptions(): Options {
    return {
      chart: {
        height: 700,
      },
      legend: restrictLastLegendItemClick(),
      plotOptions: {
        series: {
          marker: {
            enabled: false,
          },
        },
      },
      tooltip: {
        useHTML: true,
        pointFormatter() {
          return `
            ${BeaconChartBase.tooltipPointHTML(this, this.series.name, `${this.y}%`)}
            ${BeaconChartBase.multiLevelTooltipPointHTML(this.date, '', true)}
          `;
        },
      },
      exporting: {
        enabled: false,
      },
      xAxis: {
        labels: {
          step: 1,
        },
        type: 'datetime',
        dateTimeLabelFormats: {
          year: '%Y',
        },
        tickInterval: Date.UTC(2010, 0, 1) - Date.UTC(2009, 0, 1),
      },
      yAxis: {
        endOnTick: false,
        tickInterval: 10,
      },
    };
  }

  render() {
    return this.renderComponent(
      html`
        <section class="sector-diversification-chart-content">
          <beacon-area-chart
            stacking="normal"
            y-axis-label="${Constants.Y_AXIS_LABEL}"
            x-axis-label="${Constants.X_AXIS_LABEL}"
            series="${JSON.stringify(this.getSeriesData())}"
            options="${JSON.stringify(this.getOptions())}"
            tooltip-variant="multi-level">
          </beacon-bar-chart>
        </section>
      `,
    );
  }
}
